import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { TranscriptCopyButton } from "@/components/conversation/details/TranscriptCopyButton";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Badge } from "@/components/ui/badge";
import { ConversationDetails } from "@/types/conversation/conversation";
import { formatDuration as utilFormatDuration } from "@/utils/formatting/general";

interface ConversationAnalysisExpandedProps {
  conversation: ConversationDetails;
}

const SENTIMENT_STYLES: Record<string, { label: string; chip: string; dot: string }> = {
  positive: {
    label: "Positive",
    chip: "bg-[#4E9CFF]/10 text-[#4E9CFF] border-[#4E9CFF]/30",
    dot: "bg-[#4E9CFF]",
  },
  neutral: {
    label: "Neutral",
    chip: "bg-gray-100 text-gray-700 border-gray-200",
    dot: "bg-gray-400",
  },
  negative: {
    label: "Needs attention",
    chip: "bg-[#FF4DB8]/10 text-[#FF4DB8] border-[#FF4DB8]/30",
    dot: "bg-[#FF4DB8]",
  },
};

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatPercentage = (value?: number) => {
  if (value === undefined || value === null) return null;
  return `${Math.round(value)}%`;
};

export const ConversationAnalysisExpanded = ({
  conversation,
}: ConversationAnalysisExpandedProps) => {
  const analysisResult = conversation.analysis_result || {};
  const sentiment = analysisResult.sentiment_analysis;
  const talkMetrics = analysisResult.talk_metrics;
  const keyInsights: string[] = analysisResult.key_insights || [];
  const sentimentKey = sentiment?.overall_sentiment_type || "neutral";
  const sentimentStyle = SENTIMENT_STYLES[sentimentKey] || SENTIMENT_STYLES.neutral;

  const agentTalkPct = formatPercentage(talkMetrics?.agent_talk_percentage);
  const customerTalkPct = formatPercentage(talkMetrics?.customer_talk_percentage);
  const silencePct = formatPercentage(talkMetrics?.silence_percentage);
  const satisfactionPct = formatPercentage(sentiment?.customer_satisfaction_score);

  const summary =
    conversation.summary ||
    analysisResult.summary ||
    analysisResult.brief_summary ||
    null;
  const topTopics = (conversation.topics || []).slice(0, 6);
  const topInsights = keyInsights.slice(0, 3);
  const leadFieldCount = conversation.lead_info
    ? Object.keys(conversation.lead_info).length
    : 0;
  const transcriptCount = Array.isArray(conversation.transcript)
    ? conversation.transcript.length
    : 0;

  const primaryColor = conversation.genie_branding?.primaryColor || "#4E9CFF";

  return (
    <div className="bg-[#F9FAFC] border-t border-gray-200">
      <div className="p-5 md:p-6">
        {/* Header row: genie + quick stats */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <GenieAvatar
              size={44}
              color={primaryColor}
              secondaryColor={conversation.genie_branding?.secondaryColor}
              customAvatarUrl={
                conversation.genie_branding?.logoUrl ||
                conversation.genie_branding?.customAvatarUrl
              }
              gradientEnabled={conversation.genie_branding?.gradientEnabled}
              gradientAngle={conversation.genie_branding?.gradientAngle}
              avatarBackgroundOpacity={conversation.genie_branding?.avatarBackgroundOpacity}
            />
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {conversation.genie_name || "Unknown Genie"}
              </h3>
              <p className="text-xs text-gray-500">
                {conversation.conversation_date
                  ? formatDate(conversation.conversation_date)
                  : "Date unknown"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 text-center">
            <Stat label="Duration" value={
              conversation.duration_seconds
                ? utilFormatDuration(conversation.duration_seconds)
                : "—"
            } />
            <Divider />
            <Stat label="Messages" value={transcriptCount > 0 ? String(transcriptCount) : "—"} />
            <Divider />
            <Stat label="Lead fields" value={leadFieldCount > 0 ? String(leadFieldCount) : "—"} />
          </div>
        </div>

        {/* Snapshot row: sentiment + talk-time bar */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-5">
          {/* Sentiment chip */}
          <div className="md:col-span-2 bg-white rounded-2xl border border-gray-200 p-4">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-2">
              Sentiment
            </div>
            <div className="flex items-center gap-3 mb-1">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${sentimentStyle.chip}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${sentimentStyle.dot}`} />
                {sentimentStyle.label}
              </span>
              {satisfactionPct && (
                <span className="text-xs text-gray-600">
                  Satisfaction <span className="font-semibold text-gray-900">{satisfactionPct}</span>
                </span>
              )}
            </div>
          </div>

          {/* Talk-time bar */}
          <div className="md:col-span-3 bg-white rounded-2xl border border-gray-200 p-4">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-2">
              Talk time
            </div>
            {talkMetrics ? (
              <>
                <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100">
                  <div
                    className="bg-[#4E9CFF]"
                    style={{ width: `${talkMetrics.agent_talk_percentage || 0}%` }}
                  />
                  <div
                    className="bg-[#FF4DB8]"
                    style={{ width: `${talkMetrics.customer_talk_percentage || 0}%` }}
                  />
                  <div
                    className="bg-gray-300"
                    style={{ width: `${talkMetrics.silence_percentage || 0}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2 text-[11px] text-gray-600">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#4E9CFF]" />
                    Genie <span className="font-semibold text-gray-900">{agentTalkPct ?? "—"}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF4DB8]" />
                    Customer <span className="font-semibold text-gray-900">{customerTalkPct ?? "—"}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                    Silence <span className="font-semibold text-gray-900">{silencePct ?? "—"}</span>
                  </span>
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-500 italic">No talk-time data yet</p>
            )}
          </div>
        </div>

        {/* Summary + insights */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          {/* Summary */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-2">
              Summary
            </div>
            {summary ? (
              <p className="text-sm text-gray-700 leading-relaxed line-clamp-4">{summary}</p>
            ) : (
              <p className="text-xs text-gray-500 italic">No summary yet</p>
            )}
          </div>

          {/* Top insights */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-2">
              Top insights
            </div>
            {topInsights.length > 0 ? (
              <ul className="space-y-1.5">
                {topInsights.map((insight, idx) => (
                  <li key={idx} className="text-xs text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="w-1 h-1 mt-1.5 rounded-full bg-[#FFBB33] flex-shrink-0" />
                    <span className="line-clamp-2">{insight}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-gray-500 italic">No insights yet</p>
            )}
          </div>
        </div>

        {/* Topics + CTA row */}
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 min-w-0">
            {topTopics.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {topTopics.map((topic, idx) => (
                  <Badge
                    key={idx}
                    variant="secondary"
                    className="bg-white border border-gray-200 text-gray-700 font-medium"
                  >
                    {topic}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic">No topics extracted yet</p>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <TranscriptCopyButton
              genieName={conversation.genie_name ?? undefined}
              dateRaw={conversation.conversation_date}
              durationSeconds={conversation.duration_seconds}
              rawTranscript={conversation.transcript}
            />
            {conversation.conversation_id && (
              <Link
                to={`/genie-hub/conversations/${conversation.conversation_id}`}
                onClick={(e) => e.stopPropagation()}
                className="group inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-md shadow-genie-primary/30 hover:shadow-lg hover:shadow-genie-primary/40 hover:scale-[1.02] transition-all flex-shrink-0"
                style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
              >
                <span>Open full conversation</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div className="text-sm font-semibold text-gray-900 tabular-nums">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
  </div>
);

const Divider = () => <span className="w-px h-8 bg-gray-200" />;
